import React from 'react';
import { useIntl } from 'react-intl';

import IconButton from '@atlaskit/button/icon/button';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import GenerativeIndicatorIcon from '@atlaskit/icon-lab/core/generative-indicator';

import { useAIRovoButtonEnablement } from '@confluence/ai-common/entry-points/useAIRovoButtonEnablement.ts';
import { fg } from '@confluence/feature-gating';
import { LoadableAfterPaint, type LoadableType } from '@confluence/loadable';

import { reviewButtonMessages } from './reviewButtonMessages.ts';

import type { ReviewButton } from './ReviewButton.tsx';

// Match the loaded button while keeping its implementation behind the dynamic import.
export function ReviewButtonPlaceholder(): React.JSX.Element {
	const { formatMessage } = useIntl();
	const isRovoButtonEnabled = useAIRovoButtonEnablement();
	const label = formatMessage(reviewButtonMessages.suggestionsLabel);
	return (
		<Tooltip content={label} position="left" delay={0}>
			<IconButton
				appearance="subtle"
				icon={GenerativeIndicatorIcon}
				label={label}
				shape={isRovoButtonEnabled ? 'default' : 'circle'}
				isDisabled
				testId="object-sidebar-review-button"
			/>
		</Tooltip>
	);
}

export const ReviewButtonLoader: LoadableType<React.ComponentProps<typeof ReviewButton>> =
	LoadableAfterPaint({
		loading: () =>
			fg('confluence_sidebar_buttons_initial_render') ? <ReviewButtonPlaceholder /> : null,
		loader: async () =>
			(await import(/* webpackChunkName: "loadable-ReviewButton" */ './ReviewButton.tsx'))
				.ReviewButton,
	});
